import { useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notifyError, notifySuccess, notifyInfo } from '@/utils/notify';
import { subDays } from 'date-fns';

export interface Notification {
  id: string;
  titulo: string;
  mensagem: string;
  tipo: 'info' | 'success' | 'warning' | 'error';
  link: string | null;
  lida: boolean;
  criado_em: string;
}

// Seleção explícita — apenas colunas consumidas pelo componente
const NOTIFICACAO_SELECT = 'id, titulo, mensagem, tipo, link, lida, criado_em';
const CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24h

const playNotificationSound = () => {
  try {
    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3');
    audio.volume = 0.4;
    audio.play().catch(() => {});
  } catch {
    /* noop */
  }
};

const shouldRunCleanup = (userId: string): boolean => {
  try {
    const key = `notif-cleanup-${userId}`;
    const last = Number(localStorage.getItem(key) || 0);
    if (Date.now() - last < CLEANUP_INTERVAL_MS) return false;
    localStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const queryKey = useMemo(() => ['notificacoes', userId] as const, [userId]);

  const { data: notifications = [], isLoading: loading, refetch, isSuccess } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    queryFn: async ({ signal }): Promise<Notification[]> => {
      // Timeout client-side de 8s — protege a UI caso o RLS estoure
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const abortSignal = signal ?? controller.signal;
      try {
        const { data, error } = await supabase
          .from('notificacoes')
          .select(NOTIFICACAO_SELECT)
          .eq('usuario_id', userId!)
          .order('criado_em', { ascending: false })
          .limit(20)
          .abortSignal(abortSignal);

        if (error) {
          const code = (error as { code?: string }).code;
          // RLS recursivo / statement timeout → degrada para vazio sem quebrar UI
          if (code === '57014' || code === '54001') {
            if (import.meta.env.DEV) {
              console.warn('[useNotifications] RLS/timeout, devolvendo []:', code, error.message);
            }
            return [];
          }
          if (import.meta.env.DEV) console.error('[useNotifications] Erro ao buscar notificações:', error);
          throw error;
        }
        return (data ?? []) as Notification[];
      } finally {
        clearTimeout(timeoutId);
      }
    },
  });

  // Limpeza de notificações antigas — só roda após sucesso e 1x/24h por usuário
  useEffect(() => {
    if (!userId || !isSuccess || !shouldRunCleanup(userId)) return;

    const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
    supabase
      .from('notificacoes')
      .delete()
      .eq('usuario_id', userId)
      .eq('lida', true)
      .lt('criado_em', thirtyDaysAgo)
      .then(({ error }) => {
        if (error && import.meta.env.DEV) {
          console.error('[useNotifications] Erro na limpeza de notificações:', error);
        }
      });
  }, [userId, isSuccess]);

  // Realtime: atualiza cache diretamente em vez de refetch (defensivo)
  useEffect(() => {
    if (!userId) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    try {
      channel = supabase
        .channel(`notificacoes-user-${userId}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'notificacoes', filter: `usuario_id=eq.${userId}` },
          (payload) => {
            try {
              const novo = payload.new as Notification;
              queryClient.setQueryData<Notification[]>(queryKey, (prev = []) => {
                if (prev.some((n) => n.id === novo.id)) return prev;
                return [novo, ...prev].slice(0, 20);
              });
              playNotificationSound();
              notifyInfo(novo.titulo, novo.mensagem);
            } catch (err) {
              if (import.meta.env.DEV) console.warn('[useNotifications] erro processando realtime:', err);
            }
          }
        )
        .subscribe();
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[useNotifications] falha ao assinar realtime:', err);
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [userId, queryClient, queryKey]);

  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notificacoes')
        .update({ lida: true, lida_em: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<Notification[]>(queryKey);
      queryClient.setQueryData<Notification[]>(queryKey, (curr = []) =>
        curr.map((n) => (n.id === id ? { ...n, lida: true } : n))
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      notifyError('Erro', 'Não foi possível marcar como lida.');
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!userId) return;
      const { error } = await supabase
        .from('notificacoes')
        .update({ lida: true, lida_em: new Date().toISOString() })
        .eq('usuario_id', userId)
        .eq('lida', false);
      if (error) throw error;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<Notification[]>(queryKey);
      queryClient.setQueryData<Notification[]>(queryKey, (curr = []) =>
        curr.map((n) => ({ ...n, lida: true }))
      );
      return { prev };
    },
    onSuccess: () => {
      notifySuccess('Pronto', 'Todas as notificações foram marcadas como lidas.');
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      notifyError('Erro', 'Não foi possível marcar todas como lidas.');
    },
  });

  const markAsRead = useCallback((id: string) => markAsReadMutation.mutate(id), [markAsReadMutation]);
  const markAllAsRead = useCallback(() => markAllAsReadMutation.mutate(), [markAllAsReadMutation]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.lida).length, [notifications]);

  return {
    notifications,
    unreadCount,
    /** @deprecated use `isLoading` (Lumina Core contract) */
    loading,
    isLoading: loading,
    markAsRead,
    markAllAsRead,
    refresh: refetch,
    refetch,
  };
};
